import React, { Fragment } from 'react';
import { SecondaryText } from 'Components/Texts';
import { trackEvent, TRACKING_ENUMS } from 'Utils/Analytics';
import { SHEER_FIRST_RESPONDER_TYPE, SHEER_MILITARY_TYPE } from 'Utils/Constants';
import { SmartLink } from 'Components/SmartLink';
import { getDefaultConfig, getState } from 'Utils/Store';
import { getSheerIdProgram } from 'Utils/SheerIdHelpers';

const TermsOfServiceLink = (props) => {
  const { termsOfUsePage } = getDefaultConfig();
  return <SmartLink linkUrl={termsOfUsePage} target="noopener noreferrer" reload {...props} />;
};
const PrivacyPolicyLink = (props) => {
  const { privacyPolicyPage } = getDefaultConfig();
  return <SmartLink linkUrl={privacyPolicyPage} target="noopener noreferrer" reload {...props} />;
};
export const SheerIdHelpLink = (props) => {
  let sheerProgramType = getSheerIdProgram();
  if (!sheerProgramType) {
    const urlParams = new URLSearchParams(location.search);
    sheerProgramType = urlParams.get('sheerVerification') || SHEER_MILITARY_TYPE;
  }

  const { sheerIdHelpMilitaryPage, sheerIdHelpFirstResponderPage } = getDefaultConfig();
  const linkUrl =
    sheerProgramType === SHEER_FIRST_RESPONDER_TYPE ? sheerIdHelpFirstResponderPage : sheerIdHelpMilitaryPage;
  return <SmartLink linkUrl={linkUrl} target="noopener noreferrer" reload {...props} />;
};

const MyAccountLink = ({ text }) => {
  const routes = getState('appConfig')?.routes;
  return FinePrintLink({ text, linkUrl: routes.myAccount });
};

const ContactCustomerServiceLink = ({ text }) => {
  return FinePrintLink({ text, linkUrl: 'https://help.fox.com' });
};

const FinePrintLink = ({ text, linkUrl }) => {
  return <SmartLink linkUrl={linkUrl} target="noopener noreferrer" reload className="finePrintLink" text={text} />;
};

export const TosAndPrivacyFinePrint = ({ ctaText = '' }) => {
  if (ctaText) {
    const tosAndPrivacyText = (
      <Fragment>
        By clicking &quot;{ctaText}&quot;, you agree to the{' '}
        <TermsOfServiceLink
          text="Terms of Use"
          onClick={() => trackEvent(TRACKING_ENUMS.EVENT_TERMS_OF_SERVICE, null, { profile_start_source: 'signup' })}
          className="blueLink"
        />
        {', '}
        <PrivacyPolicyLink
          text="Privacy Policy"
          onClick={() => trackEvent(TRACKING_ENUMS.EVENT_PRIVACY_POLICY, null, { profile_start_source: 'signup' })}
          className="blueLink"
        />
        , and agree to receive content and promotional communications
      </Fragment>
    );
    return <SecondaryText className="legalText" alignText="centerText" text={tosAndPrivacyText} />;
  }
  return null;
};

export const MarineFinePrint = ({ ctaText = '' }) => {
  if (ctaText) {
    const marineLegalText = (
      <Fragment>
        By clicking &quot;{ctaText}&quot;, you agree to the{' '}
        <TermsOfServiceLink
          text="Terms of Use"
          onClick={() => trackEvent(TRACKING_ENUMS.EVENT_TERMS_OF_SERVICE)}
          className="finePrintLink"
        />
        {', '}
        <PrivacyPolicyLink
          text="Privacy Policy"
          onClick={() => trackEvent(TRACKING_ENUMS.EVENT_PRIVACY_POLICY)}
          className="finePrintLink"
        />
        , and agree to receive content and promotional communications from FOX NEWS.
      </Fragment>
    );
    return <p className="finePrint centerText">{marineLegalText}</p>;
  }
  return null;
};

export const RedeemGiftHomeFinePrint = ({ className = '' }) => {
  return (
    <p className={className}>
      Gift subscriptions are valid only toward the Fox Nation streaming service in the United States. The Fox Nation
      service is not available in other countries outside of the United States and gift subscriptions are not redeemable
      outside the United States. New users must create an account to redeem. Existing users can apply a gift
      subscription only to an existing paid account that was created on Foxnation.com. Gift subscriptions cannot be
      applied to trial accounts. Gift subscriptions cannot be applied to Fox Nation accounts purchased on Comcast or
      other third-party TV providers that offer Fox Nation directly to their subscribers. Gift subscriptions cannot be
      applied to existing Fox Nation accounts purchased through the Fox Nation apps on the following platforms: iOS,
      Android, Apple TV, Xbox, Roku, Fire TV or TVOS. A maximum of three gift subscriptions can be credited to an
      account at a time. Entire value is credited to your Fox Nation account upon redemption. Gift subscriptions are
      nonrefundable and are not redeemable for cash, except where required by law. Gift subscription codes cannot be
      resold or replaced if they are misplaced, lost or stolen. Fox News Network, LLC is not responsible for misplaced,
      lost or stolen codes or for unauthorized use. When the Fox Nation gift subscription period has ended, access to
      Fox Nation will end unless you provide another payment method and sign up for a paid subscription plan. The
      merchandise associated with each subscription or gift subscription tier is subject to change. Visit{' '}
      <SmartLink
        linkUrl="https://nation.foxnews.com/gift-terms"
        text="https://nation.foxnews.com/gift-terms"
        className="finePrintLink"
        reload
        target="noopener noreferrer"
      />{' '}
      for full terms and conditions.
    </p>
  );
};

export const RedeemGiftFinePrint = ({ className = '' }) => {
  return (
    <Fragment>
      <p className={`finePrint ${className}`}>
        This card will be redeemed to the account you are currently signed into. If you want it applied to a new or
        different account, sign out and start the redemption process over.
      </p>
      <p className={`finePrint ${className}`}>
        Learn how to find your gift code{' '}
        <SmartLink
          linkUrl="https://help.fox.com/s/article/How-Do-I-Redeem-My-Fox-Nation-Gift-Card"
          text="here"
          className="finePrintLink"
          target="noopener noreferrer"
          reload
        />
        .
      </p>
      <p className={`finePrint ${className}`}>
        If you need additional help, please contact{' '}
        <SmartLink
          linkUrl="https://help.fox.com"
          text="Customer Support"
          className="finePrintLink"
          target="noopener noreferrer"
          reload
        />
        .
      </p>
    </Fragment>
  );
};

export const RedeemGiftTrialistFinePrint = ({ className = '' }) => {
  return <p className={`finePrint ${className}`}>Please redeem your Gift card at the end of your trial.</p>;
};

export const RedeemGiftIAPFinePrint = ({ className = '' }) => {
  return (
    <p className={`finePrint ${className}`}>
      Gift subscriptions cannot be applied to existing Fox Nation accounts purchased through the Fox Nation apps on iOS,
      Android, Roku, Fire, TV or tvOS. See our{' '}
      <SmartLink
        linkUrl="http://nation.foxnews.com/gift-terms"
        text="Gift Terms"
        className="finePrintLink"
        target="noopener noreferrer"
        reload
      />{' '}
      for more information.
    </p>
  );
};

export const ReCaptchaFinePrint = ({ className = '' }) => {
  return (
    <p className={`finePrint ${className}`} data-testid="recaptchaFinePrint">
      This site is protected by reCAPTCHA and the Google{' '}
      <SmartLink
        linkUrl="https://policies.google.com/privacy"
        text="Privacy Policy"
        className="finePrintLink"
        target="noopener noreferrer"
        reload
      />{' '}
      and{' '}
      <SmartLink
        linkUrl="https://policies.google.com/terms"
        text="Terms of Service"
        className="finePrintLink"
        target="noopener noreferrer"
        reload
      />{' '}
      apply.
    </p>
  );
};

export const UpgradePlanPaymentFinePrint = ({ ctaText }) => {
  return (
    <GeneralMarketPaymentFinePrint
      ctaText={ctaText}
      prependedChild={
        <p>
          When you upgrade your plan, you will be billed the full annual fee and your Annual plan will begin
          immediately. Any remaining time on your current plan will be applied at the end of your Annual plan.
        </p>
      }
    />
  );
};

export const DowngradePlanPaymentFinePrint = ({ ctaText }) => {
  return (
    <GeneralMarketPaymentFinePrint
      ctaText={ctaText}
      prependedChild={<p>When you downgrade your plan, you will be billed at the end of your current plan.</p>}
    />
  );
};

export const SheerPaymentFinePrint = ({ ctaText }) => {
  return (
    <div className="finePrint paymentFinePrint">
      <p>
        By entering your information and clicking the &quot;{ctaText}&quot; button (below) or otherwise signing up, you
        agree to the Fox Nation <TermsOfServiceLink text="Terms of Use" className="finePrintLink" /> and{' '}
        <PrivacyPolicyLink text="Privacy Policy" className="finePrintLink" /> and agree to receive content and
        promotional communications. You can opt-out of such communications at any time.
      </p>
      <p>
        <b>Automatic Renewal Notice:</b> This is an automatically renewing subscription. If a free trial or initial
        promotional discount is offered, the subscription will automatically convert into a recurring paid subscription
        at the end of the offer period, at Fox Nation's then-current First Responder/Military discounted rate (plus
        applicable tax), unless you cancel at least 24 hours before the conversion. Your Fox Nation subscription will
        then automatically renew on a recurring basis until you tell us to stop. Fox Nation is pleased to offer First
        Responder/Military members an ongoing subscription discount, which is currently fifty percent (50%) off its
        standard full-price subscription rate (though both the discount and underlying rate are subject to change). As
        such, annual plans will renew for additional one-year periods, and you will be charged at the start of each year
        at the then-current First Responder/Military discounted annual subscription rate (currently 50% off the current
        full-price rate of $71.49/year — a total of $35.49, but subject to change). Subscription fees are
        non-refundable. You must adjust your automatic renewal settings, or cancel, at least 24 hours prior to your
        auto-renewal date. You can cancel your subscription at any time, by logging into your{' '}
        <MyAccountLink text="My Account" /> page and selecting "Cancel Plan," and can{' '}
        <ContactCustomerServiceLink text="contact customer service" /> for assistance or more information. Canceling
        your subscription will prevent future auto-renewals, but will not refund your subscription — you will continue
        to have access to Fox Nation for the remainder of time you have already paid for. Renewal charges will be
        applied to the payment method associated with your account, and you hereby authorize Fox News Network, LLC or
        its designee to make such charge.
      </p>
      <p>
        Promotional offers are available only to new and returning Fox Nation subscribers on qualifying Fox Nation
        subscription plans, may be redeemed only once per person/account, are valid only for a limited time, are subject
        to change without notice, and cannot be combined with any other offer, promotion or discount.
      </p>
    </div>
  );
};

export const GeneralMarketPaymentFinePrint = ({ ctaText, prependedChild }) => {
  return (
    <div className="finePrint paymentFinePrint">
      {prependedChild}
      <p>
        By entering your information and clicking the &quot;{ctaText}&quot; button (below) or otherwise signing up, you
        agree to the Fox Nation <TermsOfServiceLink text="Terms of Use" className="finePrintLink" /> and{' '}
        <PrivacyPolicyLink text="Privacy Policy" className="finePrintLink" /> and agree to receive content and
        promotional communications. You can opt-out of such communications at any time.
      </p>
      <p>
        <b>Automatic Renewal Notice:</b> This is an automatically renewing subscription. If a free trial or promotional
        discount is offered, the subscription will automatically convert into a full-price paid subscription at the end
        of the offer period at the then-current rate (plus applicable tax), unless you cancel at least 24 hours before
        the conversion. After the initial period, your Fox Nation subscription will automatically renew on a recurring
        basis until you tell us to stop. Annual plans will renew for additional one-year periods, and you will be
        charged at the start of each year at the then-current full-price annual subscription rate (currently
        $71.49/year, but subject to change). Monthly plans will renew for additional one-month periods, and you will be
        charged at the start of each month at the then-current full-price monthly subscription rate (currently
        $8.99/month, but subject to change). Subscription fees are non-refundable. You must adjust your automatic
        renewal settings, or cancel, at least 24 hours prior to your auto-renewal date. You can cancel your subscription
        at any time, by logging into your <MyAccountLink text="My Account" /> page and selecting “Cancel Plan,” and can{' '}
        <ContactCustomerServiceLink text="contact customer service" /> for assistance or more information. Canceling
        your subscription will prevent future auto-renewals, but will not refund your subscription — you will continue
        to have access to Fox Nation for the remainder of time you have already paid for. Renewal charges will be
        applied to the payment method associated with your account, and you hereby authorize Fox News Network, LLC or
        its designee to make such charge.
      </p>
      <p>
        Promotional offers are available only to new and returning Fox Nation subscribers on qualifying Fox Nation
        subscription plans, may be redeemed only once per person/account, are valid only for a limited time, are subject
        to change without notice, and cannot be combined with any other offer, promotion or discount.
      </p>
    </div>
  );
};

export const VerificationFinePrint = () => (
  <div className="finePrint sheerIdFinePrint">
    By clicking, I consent to processing my information for the purpose of verifying my eligibility as described within
    the <PrivacyPolicyLink text="Fox Nation Privacy Policy" className="finePrintLink highlighted" />. I understand that
    my information will be shared with Fox Nation. Verification services powered by SheerID{' '}
    <SheerIdHelpLink text="SheerID Help Center" className="finePrintLink highlighted" />.
  </div>
);

export const VerificationReviewFinePrint = () => (
  <div className="finePrint sheerIdFinePrint">
    Verification services powered by SheerID{' '}
    <SheerIdHelpLink text="SheerID Help Center" className="finePrintLink highlighted" />.
  </div>
);
